/**
 * The homepage's build-time data (`WEB-039`, canon decision `D-015`).
 *
 * `dealbid.app` is generated at build time: the board, both click-ranked lists and the
 * Founding cohort are read by the build and embedded here, so a visitor's page load makes no
 * request for them. The one read that stays live is `/v1/auction/tomorrow` — the only part of
 * the page whose subject moves within a display day, because canon § 7 locks the board's
 * positions for the whole of it.
 *
 * The values arrive through `virtual:dealbid/site-data`, which `vite.config.ts` fills: from
 * core during a build or a dev server, from a committed fixture under `test`.
 */
import type {
  BoardResponse,
  FoundingResponse,
  RankingResponse,
} from "./api/types";

export interface SiteData {
  board: BoardResponse;
  weekly: RankingResponse;
  monthly: RankingResponse;
  founding: FoundingResponse;
  /** When the build read them, in UTC. */
  builtAt: string;
  /**
   * Which environment's core they came from. Never rendered — it exists so
   * `scripts/deploy-config/rules.mjs` can refuse a bundle whose data and whose compiled API
   * base URL disagree.
   */
  builtFrom: string;
}

/*
 * There is no `SITE_DATA` constant any more (`WEB-044`, canon decision `D-020`).
 *
 * The reads used to arrive through a Vite virtual module, which meant any component could
 * import the whole site's data from anywhere. They now arrive as a prop: `app/page.tsx` is a
 * server component, it performs the reads at build time, and it hands the result down.
 *
 * That is a better shape for the same guarantee. The shape is still checked in Node before it
 * reaches the bundle — `scripts/site-data/reads.mjs` fails the build on a response that is not
 * the contract — and now the only way a component sees the data is by being given it, so the
 * suite drives every board state by passing one rather than by intercepting a module.
 */

/** Today's UTC date as `YYYY-MM-DD`, computed locally. Costs no request. */
export function utcDateOf(instantMs: number): string {
  return new Date(instantMs).toISOString().slice(0, 10);
}

/**
 * Whether the built board is for a day that has already ended.
 *
 * The failure this exists for: the build that should run at the auction boundary does not,
 * and the site goes on serving yesterday's board with yesterday's `displayDate` printed on
 * it, all day, with nothing to notice. Canon § 8 already makes the display day UTC-canonical
 * and the browser can compute the current UTC date with no network, so the page can tell —
 * which is why `D-015` § 3 could give this to the page rather than to a request.
 *
 * Deliberately one-directional. A built date **after** today's is not staleness: it is a
 * build that ran seconds before the boundary, read a board core had already rolled, and is
 * about to be correct. Reporting that as stale would put a warning on the page every night.
 */
export function boardIsStale(displayDate: string, nowMs: number): boolean {
  return displayDate < utcDateOf(nowMs);
}
